import { ExchangeRates } from '../types/currency';

class CurrencyService {
  private readonly API_URL = 'https://api.exchangerate-api.com/v4/latest';
  private readonly FALLBACK_API_URL = 'https://api.fixer.io/latest';
  private readonly LOCAL_STORAGE_KEY = 'exchange_rates';
  private readonly LOCAL_STORAGE_TIMESTAMP_KEY = 'exchange_rates_timestamp';
  private readonly CACHE_DURATION = 1000 * 60 * 60; // 1 hour in milliseconds

  /**
   * Get exchange rates from API or cache
   */
  async getExchangeRates(baseCurrency: string = 'AED'): Promise<ExchangeRates> {
    try {
      // Check if we have cached rates that are still valid
      const cachedRates = this.getCachedRates();
      const cachedTimestamp = this.getCachedTimestamp();
      
      if (cachedRates && cachedTimestamp && Date.now() - cachedTimestamp < this.CACHE_DURATION) {
        return cachedRates;
      }

      // Fetch fresh rates
      const rates = await this.fetchRatesFromAPI(baseCurrency);
      
      // Cache the rates
      this.cacheRates(rates);
      
      return rates;
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error);
      
      // Return cached rates if available, even if expired
      const cachedRates = this.getCachedRates();
      if (cachedRates) {
        console.warn('Using cached exchange rates due to API failure');
        return cachedRates;
      }
      
      // Return fallback rates
      return this.getFallbackRates();
    }
  }

  /**
   * Fetch rates from primary API
   */
  private async fetchRatesFromAPI(baseCurrency: string): Promise<ExchangeRates> {
    try {
      const response = await fetch(`${this.API_URL}/${baseCurrency}`);
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.rates || {};
    } catch (error) {
      // Try fallback API
      console.warn('Primary API failed, trying fallback:', error);
      return await this.fetchFromFallbackAPI(baseCurrency);
    }
  }

  /**
   * Fetch rates from fallback API
   */
  private async fetchFromFallbackAPI(baseCurrency: string): Promise<ExchangeRates> {
    const response = await fetch(`${this.FALLBACK_API_URL}?base=${baseCurrency}`);
    
    if (!response.ok) {
      throw new Error(`Fallback API responded with status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.rates || {};
  }

  /**
   * Convert amount from one currency to another
   */
  convertCurrency(
    amount: number,
    fromCurrency: string,
    toCurrency: string,
    exchangeRates: ExchangeRates
  ): number {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    // If converting from base currency (AED)
    if (fromCurrency === 'AED') {
      const rate = exchangeRates[toCurrency];
      return rate ? amount * rate : amount;
    }

    // If converting to base currency (AED)
    if (toCurrency === 'AED') {
      const rate = exchangeRates[fromCurrency];
      return rate ? amount / rate : amount;
    }

    // Converting between two non-base currencies
    const fromRate = exchangeRates[fromCurrency];
    const toRate = exchangeRates[toCurrency];
    
    if (fromRate && toRate) {
      // Convert to AED first, then to target currency
      const aedAmount = amount / fromRate;
      return aedAmount * toRate;
    }

    return amount; // Return original amount if rates not available
  }

  /**
   * Format currency amount with proper symbol and locale
   */
  formatCurrency(amount: number, currencyCode: string, symbol: string, locale: string = 'en-US'): string {
    try {
      // Use Intl.NumberFormat for proper formatting
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

      return formatter.format(amount);
    } catch (error) {
      // Fallback formatting if Intl.NumberFormat fails
      console.warn('Currency formatting failed, using fallback:', error);
      return `${symbol}${amount.toLocaleString(locale, { 
        minimumFractionDigits: 0, 
        maximumFractionDigits: 2 
      })}`;
    }
  }

  /**
   * Get cached exchange rates from localStorage
   */
  private getCachedRates(): ExchangeRates | null {
    try {
      const cached = localStorage.getItem(this.LOCAL_STORAGE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Failed to get cached rates:', error);
      return null;
    }
  }

  /**
   * Get cached timestamp from localStorage
   */
  private getCachedTimestamp(): number | null {
    try {
      const timestamp = localStorage.getItem(this.LOCAL_STORAGE_TIMESTAMP_KEY);
      return timestamp ? parseInt(timestamp, 10) : null;
    } catch (error) {
      console.error('Failed to get cached timestamp:', error);
      return null;
    }
  }

  /**
   * Cache exchange rates in localStorage
   */
  private cacheRates(rates: ExchangeRates): void {
    try {
      localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(rates));
      localStorage.setItem(this.LOCAL_STORAGE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Failed to cache exchange rates:', error);
    }
  }

  /**
   * Get fallback exchange rates (approximate rates for when API fails)
   */
  private getFallbackRates(): ExchangeRates {
    return {
      EUR: 0.85,
      GBP: 0.73,
      AED: 3.67,
      SAR: 3.75,
      JPY: 110.0,
      CNY: 6.45,
      INR: 74.5,
      CAD: 1.25,
      AUD: 1.35,
      CHF: 0.92,
      SEK: 8.5,
      NOK: 8.8,
      DKK: 6.3,
      PLN: 3.9,
      TRY: 8.5,
      RUB: 75.0,
      BRL: 5.2,
      MXN: 20.0,
      ZAR: 14.5,
      SGD: 1.35,
      HKD: 7.8,
      NZD: 1.4,
      KRW: 1180.0,
      THB: 32.0,
      MYR: 4.2,
      IDR: 14250.0,
      PHP: 50.0,
      VND: 23000.0,
      EGP: 15.7,
    };
  }

  /**
   * Clear cached exchange rates
   */
  clearCache(): void {
    try {
      localStorage.removeItem(this.LOCAL_STORAGE_KEY);
      localStorage.removeItem(this.LOCAL_STORAGE_TIMESTAMP_KEY);
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }
}

export const currencyService = new CurrencyService();